import {
  HexIcon,
  Icons,
  Spinner,
  Placeholder as StatusMessage,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { PreviewYaml } from 'components/code';

import { FailedToLoad } from '@cast/shared-messages-ui';

type ManifestTabContentProps = {
  data?: { object?: unknown } | null;
  error?: Error | null;
  refetch?: () => void;
  isLoading?: boolean;
  isCustom?: boolean;
  testId: string;
  emptyStateTitle?: string;
  loadingTitle?: string;
};

export const ManifestTabContent = ({
  data,
  error,
  refetch,
  isLoading,
  isCustom,
  testId,
  emptyStateTitle = 'Manifest unavailable',
  loadingTitle,
}: ManifestTabContentProps) => {
  const renderContent = () => {
    if (isCustom) {
      return (
        <Stack
          alignItems="center"
          data-testid="empty-state"
          spacing={2}
          mt={80}
        >
          <StatusMessage
            icon={
              <HexIcon
                variant="notification"
                size={71}
                icon={<Icons.XCircle />}
              />
            }
            title={emptyStateTitle}
            sx={{
              mt: 24,
              color: tokens.theme.fg.structural.primaryOnInverse,
            }}
            titleProps={{ variant: 'A1' }}
          />
        </Stack>
      );
    }

    if ((isLoading || (!data && !error)) && loadingTitle) {
      return (
        <Stack gap={8} data-testid="loading">
          <Stack direction="row" gap={8}>
            <Spinner
              variant="indeterminate"
              color="tertiary"
              size={20}
              testId="connecting-spinner"
            />
            <Stack>
              <Typography
                variant="A1"
                color={tokens.theme.fg.structural.primaryOnInverse}
              >
                {loadingTitle}
              </Typography>
              <Typography
                variant="P12R"
                color={tokens.theme.fg.structural.secondaryOnInverse}
              >
                We&apos;re retrieving the details of this workload, this may
                take a few moments...
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      );
    }

    if (error) {
      return (
        <Stack mt={80}>
          <FailedToLoad compact refresh={refetch} />
        </Stack>
      );
    }

    if (data?.object) {
      return (
        <Box data-testid="yaml">
          <PreviewYaml
            data={data.object}
            size="lg"
            rootSx={{
              backgroundColor: 'transparent',
            }}
            containerSx={{
              padding: '4px 8px',
            }}
          />
        </Box>
      );
    }

    return (
      <Stack alignItems="center" data-testid="empty-state" spacing={2} mt={80}>
        <StatusMessage
          icon={
            <HexIcon
              variant="notification"
              size={71}
              icon={<Icons.XCircle />}
            />
          }
          title={emptyStateTitle}
          sx={{
            mt: 24,
            color: tokens.theme.fg.structural.primaryOnInverse,
          }}
          titleProps={{ variant: 'A1' }}
        />
      </Stack>
    );
  };

  return (
    <Stack gap={12} flex={1} data-testid={testId}>
      {renderContent()}
    </Stack>
  );
};
